import { useState } from "react";
import { FaEdit, FaTimes, FaCheck } from "react-icons/fa";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/";
import { formatForDisplay } from "../utils/dateUtils";
import { useEntryAccent } from "../hooks/useEntryAccent";

const datePickerSlotProps = {
  textField: {
    size: "small",
    fullWidth: true,
    sx: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "white",
        borderRadius: "0.75rem",
        fontSize: "0.875rem",
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9ca3af" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6b7280" },
      },
    },
  },
};

/**
 * The entry list, with an expand-to-edit row. Shared by income and expenses.
 *
 * @param {Object} props
 * @param {Object[]} props.entries
 * @param {string[]} props.categories
 * @param {import('../constants/entryKinds').EntryKind} props.kind
 * @param {'received'|'outstanding'} [props.mode]
 *   'outstanding' lists unpaid income: it keys the date column off earnedDate
 *   and offers Mark paid instead of relying on the date being set.
 * @param {(entry: Object) => void} props.onSave
 * @param {(id: string) => void} props.onDelete
 * @param {(entry: Object) => void} [props.onMarkPaid]
 */
const EntryTable = ({
  entries,
  categories,
  kind,
  mode = "received",
  onSave,
  onDelete,
  onMarkPaid,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editedEntry, setEditedEntry] = useState({});
  const [editError, setEditError] = useState("");
  const accent = useEntryAccent(kind);

  const isOutstanding = mode === "outstanding";

  // The outstanding view has no note to show, so it spends that width on the
  // Mark paid button and folds the edit affordance in beside it. A truncated
  // button reads as breakage, and the source chip has to stay legible.
  const lastSpan = isOutstanding ? "col-span-4" : "col-span-3";

  const handleEdit = (entry) => {
    setEditingId(entry._id);
    setEditedEntry({ ...entry });
    setEditError("");
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditedEntry({});
    setEditError("");
  };

  const handleSave = () => {
    const amount = Number(editedEntry.amount);

    if (editedEntry.amount === "" || Number.isNaN(amount)) {
      setEditError("Amount must be a number.");
      return;
    }
    if (amount <= 0) {
      setEditError("Amount must be greater than zero.");
      return;
    }

    onSave({ ...editedEntry, amount });
    setEditingId(null);
    setEditedEntry({});
    setEditError("");
  };

  // Unpaid income has no cash date, so fall back to when it was earned. Sorting
  // on `date` alone would throw on those rows — they are the entire content of
  // the outstanding list.
  const sortKey = (entry) => entry.date ?? entry.earnedDate ?? "";

  // Dates are YYYY-MM-DD, so a plain string sort is chronological — and unlike
  // `new Date(str)` it doesn't depend on implementation-defined parsing.
  const sortedEntries = [...entries].sort((a, b) =>
    sortKey(b).localeCompare(sortKey(a))
  );

  return (
    <div className="mt-4 bg-white rounded-2xl shadow-soft border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-12 bg-gradient-to-r from-gray-50 to-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        <div className="col-span-3 py-3 px-3">{isOutstanding ? "Earned" : "Date"}</div>
        <div className="col-span-2 py-3 px-2 text-right">Amount</div>
        <div className="col-span-3 py-3 px-2">{kind.categoryLabel}</div>
        <div className={`${lastSpan} py-3 px-2`}>{isOutstanding ? "" : "Note"}</div>
        {!isOutstanding && <div className="col-span-1 py-3 px-2"></div>}
      </div>

      {/* Rows */}
      {sortedEntries.map((entry, index) => (
        <div key={entry._id}>
          {/* Summary Row */}
          <div
            className={`grid grid-cols-12 text-sm transition-all duration-200 ${
              editingId === entry._id
                ? "bg-gray-100 border-l-4 border-l-gray-600"
                : index % 2 === 0
                ? "bg-white hover:bg-gray-50"
                : "bg-gray-50/50 hover:bg-gray-100/50"
            }`}
          >
            <div className="col-span-3 py-3 px-3 text-gray-600">
              {formatForDisplay(sortKey(entry))}
            </div>
            <div className="col-span-2 py-3 px-2 font-semibold text-gray-800 text-right">
              ${entry.amount}
            </div>
            <div className="col-span-3 py-3 px-2 text-gray-600 truncate">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${accent.chip}`}
              >
                {entry.category}
              </span>
            </div>
            <div className={`${lastSpan} py-2 px-2 text-xs flex items-center gap-1`}>
              {isOutstanding ? (
                <>
                  <button
                    onClick={() => onMarkPaid?.(entry)}
                    className="flex-1 px-2 py-1.5 rounded-lg font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-all active:scale-95 whitespace-nowrap"
                  >
                    Mark paid
                  </button>
                  <button
                    onClick={() =>
                      editingId === entry._id ? handleCancel() : handleEdit(entry)
                    }
                    className="p-1.5 shrink-0 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                    aria-label={editingId === entry._id ? "Cancel edit" : "Edit entry"}
                  >
                    {editingId === entry._id ? <FaTimes size={14} /> : <FaEdit size={14} />}
                  </button>
                </>
              ) : (
                <span className="block truncate text-gray-400 italic">
                  {entry.description}
                </span>
              )}
            </div>
            {/* Outstanding rows carry their edit button inside the action cell
                above, beside Mark paid. */}
            {!isOutstanding && (
            <div className="col-span-1 py-3 px-2 flex justify-center">
              {editingId === entry._id ? (
                <button
                  onClick={handleCancel}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                  aria-label="Cancel edit"
                >
                  <FaTimes size={14} />
                </button>
              ) : (
                <button
                  onClick={() => handleEdit(entry)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                  aria-label="Edit entry"
                >
                  <FaEdit size={14} />
                </button>
              )}
            </div>
            )}
          </div>

          {/* Expanded Edit Form */}
          {editingId === entry._id && (
            <div className="bg-gradient-to-b from-gray-100 to-white border-b border-gray-200 p-4">
              <div className="space-y-4">
                {/* Received Date */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    {kind.hasEarnedDate ? "Received" : "Date"}
                  </label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={editedEntry.date ? dayjs(editedEntry.date, "YYYY-MM-DD") : null}
                      onChange={(value) =>
                        setEditedEntry({
                          ...editedEntry,
                          // Clearing the field is meaningful for income: it puts
                          // the entry back into Outstanding.
                          date: value?.isValid() ? value.format("YYYY-MM-DD") : null,
                        })
                      }
                      slotProps={datePickerSlotProps}
                    />
                  </LocalizationProvider>
                  {kind.hasEarnedDate && !editedEntry.date && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      No received date — this stays under Outstanding.
                    </p>
                  )}
                </div>

                {/* Earned Date — income only */}
                {kind.hasEarnedDate && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Earned
                    </label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={
                          editedEntry.earnedDate
                            ? dayjs(editedEntry.earnedDate, "YYYY-MM-DD")
                            : null
                        }
                        onChange={(value) =>
                          value?.isValid() &&
                          setEditedEntry({
                            ...editedEntry,
                            earnedDate: value.format("YYYY-MM-DD"),
                          })
                        }
                        slotProps={datePickerSlotProps}
                      />
                    </LocalizationProvider>
                  </div>
                )}

                {/* Amount Field */}
                <div>
                  <label
                    htmlFor={`${entry._id}-amount`}
                    className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5"
                  >
                    Amount
                  </label>
                  <input
                    id={`${entry._id}-amount`}
                    type="number"
                    value={editedEntry.amount}
                    onChange={(e) =>
                      setEditedEntry({ ...editedEntry, amount: e.target.value })
                    }
                    className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition-all"
                  />
                </div>

                {/* Category Field */}
                <div>
                  <label
                    htmlFor={`${entry._id}-category`}
                    className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5"
                  >
                    {kind.categoryLabel}
                  </label>
                  <select
                    id={`${entry._id}-category`}
                    value={editedEntry.category}
                    onChange={(e) =>
                      setEditedEntry({ ...editedEntry, category: e.target.value })
                    }
                    className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition-all"
                  >
                    {/* An entry can reference a category that was since renamed
                        or deleted. Without this the select would fall back to
                        showing the first option while state still held the
                        original, misrepresenting what's about to be saved. */}
                    {!categories.includes(editedEntry.category) && (
                      <option value={editedEntry.category}>
                        {editedEntry.category} (removed)
                      </option>
                    )}
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description Field */}
                <div>
                  <label
                    htmlFor={`${entry._id}-note`}
                    className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5"
                  >
                    Note
                  </label>
                  <input
                    id={`${entry._id}-note`}
                    type="text"
                    value={editedEntry.description ?? ""}
                    onChange={(e) =>
                      setEditedEntry({ ...editedEntry, description: e.target.value })
                    }
                    className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition-all"
                  />
                </div>

                {editError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3">
                    {editError}
                  </p>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleSave}
                    className={`flex-1 p-3 rounded-xl text-white font-semibold bg-gradient-to-r ${accent.button} shadow-md ${accent.shadow} transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2`}
                  >
                    <FaCheck size={12} />
                    Save Changes
                  </button>
                  <button
                    onClick={() => onDelete(entry._id)}
                    className="flex-1 p-3 rounded-xl text-red-600 font-semibold bg-red-50 hover:bg-red-100 border border-red-200 transition-all duration-200 active:scale-[0.98]"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {sortedEntries.length === 0 && (
        <div className="p-8 text-center text-gray-400">
          <p className="text-sm">
            {isOutstanding ? "Nothing outstanding" : kind.emptyListText}
          </p>
        </div>
      )}
    </div>
  );
};

export default EntryTable;
